import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Plus, X, Clock, CheckCircle, AlertTriangle, Loader, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle,
  Card, Badge, Button, Input, Select, Label, FormGroup,
  Flex, Spinner, LoadingContainer, EmptyState, EmptyIcon, EmptyText, EmptyHint,
  ModalOverlay, ModalBox, ModalHeader, ModalTitle, ModalBody, ModalFooter
} from '../components/ui';
import { theme } from '../styles/theme';
import api from '../services/api';
import VisualScheduler from '../components/VisualScheduler';
import { cronToDescription, isValidCron } from '../utils/cron-helpers';

const STATUS_VARIANT = {
  pending:          'neutral',
  in_progress:      'info',
  waiting_response: 'warning',
  completed:        'success',
  failed:           'danger',
  escalated:        'danger',
  cancelled:        'neutral'
};

const STATUS_ICON = {
  pending:          <Clock size={12} />,
  in_progress:      <Loader size={12} />,
  waiting_response: <Clock size={12} />,
  completed:        <CheckCircle size={12} />,
  failed:           <AlertTriangle size={12} />,
  escalated:        <AlertTriangle size={12} />,
};

const FILTERS = ['all', 'pending', 'scheduled', 'waiting_response', 'completed', 'failed'];

const SOURCE_READONLY = ['cron', 'agent_retry', 'schedule_retry'];
const SOURCE_BADGE = {
  cron:          { label: '⏰ Cron',          variant: 'neutral' },
  agent_retry:   { label: '🔁 Auto-retry',    variant: 'neutral' },
  schedule_retry:{ label: '🔁 Auto-retry',    variant: 'neutral' },
  campaign:      { label: '📣 Campanha',       variant: 'info'    },
  agent:         { label: '🤖 Agente',         variant: 'info'    },
  manual:        { label: '👤 Manual',         variant: 'neutral' },
};

export default function TasksPage() {
  const { t }       = useTranslation('tasks');
  const { tenantId } = useParams();
  const toast        = useToast();
  const confirm      = useConfirm();

  const [tasks, setTasks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('all');
  const [expanded, setExpanded]   = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);
  const [cronConnectors, setCronConnectors] = useState([]);
  const [showCrons, setShowCrons] = useState(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSallusAdmin = user.role === 'sallus_admin';

  const homeItem = isSallusAdmin
    ? { label: 'Libben Agents', path: '/sallus/dashboard' }
    : user.role === 'manager'
      ? { label: t('home', { ns: 'common' }), path: `/manager/${tenantId}` }
      : { label: t('home', { ns: 'common' }), path: `/dashboard/${tenantId}` };

  const breadcrumbItems = [homeItem, { label: t('title') }];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // 'scheduled' filter fetches pending tasks and filters client-side for future scheduledAt
      const apiFilter = filter === 'scheduled' ? 'pending' : filter;
      const res = await api.get(`/tasks/${tenantId}${apiFilter !== 'all' ? `?status=${apiFilter}` : ''}`);
      let result = res.data.tasks || [];
      if (filter === 'scheduled') {
        const now = new Date().toISOString();
        result = result.filter(task => task.scheduledAt && task.scheduledAt > now);
      }
      setTasks(result);

      // Load cron connectors for the recurring tasks section
      const connRes = await api.get(`/connectors/${tenantId}`);
      const crons = (connRes.data.connectors || connRes.data || []).filter(c => c.type === 'cron' && c.enabled);
      setCronConnectors(crons);
    } catch (e) { toast.error(t('loading', { ns: 'common' })); }
    finally { setLoading(false); }
  }, [tenantId, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCancel = async (task) => {
    const ok = await confirm({ title: t('cancelConfirm', { title: task.title }), danger: true });
    if (!ok) return;
    setCancellingId(task.taskId);
    try {
      await api.delete(`/tasks/${tenantId}/${task.taskId}`);
      toast.success(t('cancelSuccess'));
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
    finally { setCancellingId(null); }
  };

  const handleCreate = async (data) => {
    try {
      await api.post(`/tasks/${tenantId}`, data);
      toast.success(t('createSuccess'));
      setShowModal(false);
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
  };

  const handleDeleteCron = async (connector) => {
    const ok = await confirm({ title: t('cronDeleteConfirm', { name: connector.name, defaultValue: 'Excluir tarefa recorrente "{{name}}"?' }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/connectors/${tenantId}/${connector.connectorId}`);
      toast.success(t('cronDeleted', { defaultValue: 'Tarefa recorrente excluída' }));
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
  };

  const handleToggleCron = async (connector) => {
    try {
      await api.put(`/connectors/${tenantId}/${connector.connectorId}`, { enabled: !connector.enabled });
      toast.success(connector.enabled ? t('cronPaused', { defaultValue: 'Tarefa pausada' }) : t('cronResumed', { defaultValue: 'Tarefa reativada' }));
      load();
    } catch { toast.error(t('saveError', { ns: 'common' })); }
  };

  const isScheduledTask = (task) => task.status === 'pending' && task.scheduledAt && new Date(task.scheduledAt) > new Date();

  const activeFilters = FILTERS.filter(f => {
    if (f === 'all') return true;
    if (f === 'scheduled') return tasks.some(isScheduledTask) || filter === f;
    return tasks.some(task => task.status === f) || filter === f;
  });

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            <Button variant="secondary" size="sm" onClick={load}><RefreshCw size={14} /></Button>
            <Button onClick={() => setShowModal(true)}>
              <Plus size={14} /> {t('newTask')}
            </Button>
          </Flex>
        </PageHeader>

        {/* Filtros */}
        <FilterBar>
          {activeFilters.map(f => (
            <FilterBtn key={f} $active={filter === f} onClick={() => setFilter(f)}>
              {f === 'scheduled' ? t('filter.scheduled', { defaultValue: 'Agendadas' }) : t(`filter.${f}`)}
              {f !== 'all' && (
                <FilterCount>
                  {f === 'scheduled'
                    ? tasks.filter(isScheduledTask).length
                    : tasks.filter(task => task.status === f).length}
                </FilterCount>
              )}
            </FilterBtn>
          ))}
        </FilterBar>

        {/* Tarefas Recorrentes (Cron Connectors) */}
        {cronConnectors.length > 0 && (
          <CronSection>
            <CronSectionHeader onClick={() => setShowCrons(s => !s)}>
              <CronSectionTitle>
                <RefreshCw size={14} />
                {t('recurringTasks', { defaultValue: 'Tarefas Recorrentes' })}
                <Badge variant="neutral">{cronConnectors.length}</Badge>
              </CronSectionTitle>
              {showCrons ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </CronSectionHeader>
            {showCrons && (
              <CronList>
                {cronConnectors.map(cron => (
                  <CronItem key={cron.connectorId}>
                    <CronInfo>
                      <CronName>{cron.name}</CronName>
                      <CronMeta>
                        <Badge variant="neutral">⏰ {cron.cronExpression}</Badge>
                        {cron.lastFiredAt && (
                          <MetaText>{t('lastFired', { defaultValue: 'Último' })}: {new Date(cron.lastFiredAt).toLocaleString('pt-BR')}</MetaText>
                        )}
                      </CronMeta>
                      {cron.cronObjective && (
                        <CronObjective>{cron.cronObjective.slice(0, 120)}{cron.cronObjective.length > 120 ? '...' : ''}</CronObjective>
                      )}
                    </CronInfo>
                    <Flex gap={theme.spacing[1]}>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteCron(cron)} title={t('delete', { ns: 'common' })}>
                        <X size={13} />
                      </Button>
                    </Flex>
                  </CronItem>
                ))}
              </CronList>
            )}
          </CronSection>
        )}

        {loading ? (
          <LoadingContainer><Spinner size="24px" /> {t('loading', { ns: 'common' })}</LoadingContainer>
        ) : tasks.length === 0 ? (
          <EmptyState>
            <EmptyIcon>⚡</EmptyIcon>
            <EmptyText>{t('noTasks')}</EmptyText>
            <EmptyHint>{t('noTasksHint', { defaultValue: 'Configure uma tarefa automática, como um resumo diário às 8h ou confirmação de compromissos.' })}</EmptyHint>
            <Button variant="primary" onClick={() => setShowModal(true)} style={{ marginTop: theme.spacing[4] }}>
              <Plus size={14} /> {t('newTask')}
            </Button>
          </EmptyState>
        ) : (
          <TaskList>
            {tasks.map(task => (
              <TaskCard key={task.taskId} $status={task.status}>
                <TaskHeader onClick={() => setExpanded(expanded === task.taskId ? null : task.taskId)}>
                  <Flex gap={theme.spacing[3]} style={{ flex: 1, minWidth: 0 }}>
                    <StatusDot $status={task.status} />
                    <TaskInfo>
                      <TaskTitle>{task.title}</TaskTitle>
                      <TaskMeta>
                        <Badge variant={STATUS_VARIANT[task.status] || 'neutral'}>
                          {STATUS_ICON[task.status]} {t(`status.${task.status}`)}
                        </Badge>
                        {SOURCE_BADGE[task.source] && (
                          <Badge variant={SOURCE_BADGE[task.source].variant}>
                            {SOURCE_BADGE[task.source].label}
                          </Badge>
                        )}
                        {/* 7.2: Display scheduledAt for future-scheduled tasks */}
                        {task.scheduledAt && new Date(task.scheduledAt) > new Date() && (
                          <MetaText>📅 {new Date(task.scheduledAt).toLocaleString('pt-BR')}</MetaText>
                        )}
                        {/* 7.3: Display connector name for cron-sourced tasks */}
                        {task.source === 'cron' && task.context?.connectorId && (
                          <MetaText>🔗 {task.context.connectorId}</MetaText>
                        )}
                        {/* 7.4: Display retry count for agent_retry tasks */}
                        {task.source === 'agent_retry' && task.context?._retryCount != null && task.context?._maxRetries != null && (
                          <Badge variant="warning">
                            Retry {task.context._retryCount}/{task.context._maxRetries}
                          </Badge>
                        )}
                        {task.attempts > 0 && (
                          <MetaText>{t('attempts', { count: task.attempts })}</MetaText>
                        )}
                      </TaskMeta>
                    </TaskInfo>
                  </Flex>
                  <Flex gap={theme.spacing[2]}>
                    {/* Existing cancel for non-readonly pending/waiting/in_progress tasks */}
                    {['pending', 'waiting_response', 'in_progress'].includes(task.status) && !SOURCE_READONLY.includes(task.source) && (
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleCancel(task); }} disabled={cancellingId === task.taskId}>
                        {cancellingId === task.taskId ? <Spinner size="12px" /> : <X size={13} />}
                      </Button>
                    )}
                    {/* 7.5: Cancel button for pending tasks with future scheduledAt (including cron/retry sources) */}
                    {task.status === 'pending' && task.scheduledAt && new Date(task.scheduledAt) > new Date() && SOURCE_READONLY.includes(task.source) && (
                      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); handleCancel(task); }} disabled={cancellingId === task.taskId}>
                        {cancellingId === task.taskId ? <Spinner size="12px" /> : <X size={13} />}
                      </Button>
                    )}
                    {expanded === task.taskId ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  </Flex>
                </TaskHeader>

                {expanded === task.taskId && (
                  <TaskDetail>
                    <DetailGrid>
                      {task.objective && (
                        <DetailItem style={{ gridColumn: '1 / -1' }}>
                          <DetailLabel>{t('objective')}</DetailLabel>
                          <DetailValue>{task.objective}</DetailValue>
                        </DetailItem>
                      )}
                      {task.scheduledAt && (
                        <DetailItem>
                          <DetailLabel>{t('detail.scheduledAt')}</DetailLabel>
                          <DetailValue>{new Date(task.scheduledAt).toLocaleString('pt-BR')}</DetailValue>
                        </DetailItem>
                      )}
                      {task.lastAttemptAt && (
                        <DetailItem>
                          <DetailLabel>{t('detail.lastAttempt')}</DetailLabel>
                          <DetailValue>{new Date(task.lastAttemptAt).toLocaleString('pt-BR')}</DetailValue>
                        </DetailItem>
                      )}
                      {task.result?.summary && (
                        <DetailItem style={{ gridColumn: '1 / -1' }}>
                          <DetailLabel>{t('detail.result')}</DetailLabel>
                          <ResultBox>{task.result.summary}</ResultBox>
                        </DetailItem>
                      )}
                      {task.responseReceived?.text && (
                        <DetailItem style={{ gridColumn: '1 / -1' }}>
                          <DetailLabel>{t('responseReceived')}</DetailLabel>
                          <ResultBox>{task.responseReceived.text}</ResultBox>
                        </DetailItem>
                      )}
                      <DetailItem>
                        <DetailLabel>{t('detail.createdAt')}</DetailLabel>
                        <DetailValue>{new Date(task.createdAt).toLocaleString('pt-BR')}</DetailValue>
                      </DetailItem>
                      {task.reporting?.reportTo && (
                        <DetailItem>
                          <DetailLabel>{t('detail.reportTo')}</DetailLabel>
                          <DetailValue>{task.reporting.reportTo}</DetailValue>
                        </DetailItem>
                      )}
                    </DetailGrid>
                  </TaskDetail>
                )}
              </TaskCard>
            ))}
          </TaskList>
        )}
      </PageContainer>

      {showModal && (
        <TaskFormModal
          t={t}
          tenantId={tenantId}
          onSave={handleCreate}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}

// ── TaskFormModal ─────────────────────────────────────────────────────────────

function TaskFormModal({ t, tenantId, onSave, onClose }) {
  const { t: tScheduling } = useTranslation('scheduling');
  const toast = useToast();
  const [form, setForm] = useState({ title: '', agentId: '', context: '' });
  const [agents, setAgents]   = useState([]);
  const [saving, setSaving]   = useState(false);
  const [schedule, setSchedule] = useState(null);
  const [showSchedule, setShowSchedule] = useState(false);
  const [cronError, setCronError] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    api.get(`/marketplace/tenants/${tenantId}/agents`)
      .then(r => setAgents(r.data.agents || []))
      .catch(() => {});
  }, [tenantId]);

  // Sanitiza contexto — remove padrões de prompt injection
  const sanitize = (text) => text
    .replace(/ignore\s+(all\s+)?(previous|above|prior)\s+instructions?/gi, '')
    .replace(/system\s*:/gi, '')
    .slice(0, 2000);

  // Validate cronExpression when schedule changes
  useEffect(() => {
    if (schedule?.type === 'recurring' && schedule?.cronExpression) {
      if (!isValidCron(schedule.cronExpression)) {
        setCronError(t('invalidCron', { defaultValue: 'Expressão cron inválida. Verifique a configuração do agendamento.' }));
      } else {
        setCronError('');
      }
    } else {
      setCronError('');
    }
  }, [schedule]);

  const isScheduleValid = () => {
    if (!showSchedule || !schedule || !schedule.type) return true;
    if (schedule.type === 'recurring') {
      return schedule.cronExpression && isValidCron(schedule.cronExpression);
    }
    if (schedule.type === 'one-shot') {
      return schedule.scheduledAt && schedule.isValid;
    }
    return true;
  };

  const handleSave = async () => {
    if (!form.title || !form.agentId) return;
    if (showSchedule && schedule?.type === 'recurring' && schedule?.cronExpression && !isValidCron(schedule.cronExpression)) {
      setCronError(t('invalidCron', { defaultValue: 'Expressão cron inválida. Verifique a configuração do agendamento.' }));
      return;
    }
    setSaving(true);
    try {
      if (showSchedule && schedule?.type === 'recurring' && schedule?.cronExpression) {
        // Create Cron Connector
        const connectorId = `cron_${(schedule.agentInstanceId || form.agentId)}_${Date.now()}`;
        await api.post(`/connectors/${tenantId}`, {
          connectorId,
          type: 'cron',
          name: form.title,
          cronExpression: schedule.cronExpression,
          cronObjective: form.context ? sanitize(form.context) : form.title,
          agentInstanceId: schedule.agentInstanceId || form.agentId,
          enabled: true,
        });
        toast.success(t('createSuccess'));
        onClose();
      } else if (showSchedule && schedule?.type === 'one-shot' && schedule?.scheduledAt) {
        // Create Task with scheduledAt
        await onSave({
          title:   form.title,
          agentId: schedule.agentInstanceId || form.agentId,
          context: form.context ? { notes: sanitize(form.context) } : {},
          source:  'manual',
          priority: 'normal',
          scheduledAt: schedule.scheduledAt,
        });
      } else {
        // No schedule — current behavior
        await onSave({
          title:   form.title,
          agentId: form.agentId,
          context: form.context ? { notes: sanitize(form.context) } : {},
          source:  'manual',
          priority: 'normal',
          scheduledAt: null,
        });
      }
    } catch {
      toast.error(t('saveError', { ns: 'common' }));
    } finally { setSaving(false); }
  };

  const canSave = form.title && form.agentId && !saving && !cronError && isScheduleValid();

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox maxWidth="560px" onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{t('modal.titleNew')}</ModalTitle>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label={t('close', { ns: 'common' })}>✕</Button>
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>{t('modal.agent')} *</Label>
            <Select value={form.agentId} onChange={e => set('agentId', e.target.value)}>
              <option value="">{t('modal.agentPlaceholder')}</option>
              {agents.map(a => (
                <option key={a.instanceId} value={a.instanceId}>{a.icon} {a.name}</option>
              ))}
            </Select>
          </FormGroup>

          <FormGroup>
            <Label>{t('modal.taskTitle')} *</Label>
            <Input value={form.title} onChange={e => set('title', e.target.value)} placeholder={t('modal.titlePlaceholder')} />
          </FormGroup>

          <FormGroup>
            <Label>{t('modal.context')}</Label>
            <Textarea
              rows={3}
              value={form.context}
              onChange={e => set('context', e.target.value)}
              placeholder={t('modal.contextPlaceholder')}
              maxLength={2000}
            />
            <ContextHint>{t('modal.contextHint')}</ContextHint>
          </FormGroup>

          {/* ── Collapsible Scheduling Section (6.2) ── */}
          <ScheduleToggle onClick={() => setShowSchedule(s => !s)} type="button">
            <ScheduleToggleLabel>
              <span>📅</span>
              <span>{tScheduling('scheduleSection', { defaultValue: 'Agendamento' })}</span>
            </ScheduleToggleLabel>
            {showSchedule ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </ScheduleToggle>

          {showSchedule && (
            <ScheduleSection>
              {/* ── VisualScheduler (6.3) ── */}
              <VisualScheduler
                showObjective={false}
                showAgent={false}
                agents={agents.map(a => ({ instanceId: a.instanceId, name: a.name, icon: a.icon }))}
                autoSelectAgent={false}
                onScheduleChange={setSchedule}
              />

              {/* ── Cron Preview (6.4) ── */}
              {schedule?.type === 'recurring' && schedule?.cronExpression && (
                <CronPreviewBox>
                  <CronPreviewLabel>{tScheduling('previewLabel', { defaultValue: 'Expressão gerada' })}</CronPreviewLabel>
                  <CronPreviewRaw>{schedule.cronExpression}</CronPreviewRaw>
                  <CronPreviewDesc>
                    {cronToDescription(schedule.cronExpression, tScheduling, Intl.DateTimeFormat().resolvedOptions().timeZone)}
                  </CronPreviewDesc>
                </CronPreviewBox>
              )}

              {/* ── Cron Validation Error (6.6) ── */}
              {cronError && <CronErrorText>{cronError}</CronErrorText>}
            </ScheduleSection>
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" onClick={onClose}>{t('cancel', { ns: 'common' })}</Button>
          <Button variant="primary" onClick={handleSave} disabled={!canSave}>
            {saving ? <><Spinner size="14px" /> {t('saving', { ns: 'common' })}</> : t('create', { ns: 'common' })}
          </Button>
        </ModalFooter>
      </ModalBox>
    </ModalOverlay>
  );
}

// ── Styled ────────────────────────────────────────────────────────────────────

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const FilterBar = styled.div`display: flex; gap: ${theme.spacing[2]}; margin-bottom: ${theme.spacing[5]}; flex-wrap: wrap;`;
const FilterBtn = styled.button`display: flex; align-items: center; gap: ${theme.spacing[1]}; padding: ${theme.spacing[1]} ${theme.spacing[3]}; border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.medium}; cursor: pointer; border: 1px solid ${p => p.$active ? theme.colors.primary : theme.colors.border}; background: ${p => p.$active ? theme.colors.primary : theme.colors.white}; color: ${p => p.$active ? 'white' : theme.colors.textSecondary}; transition: ${theme.transitions.fast}; &:hover { border-color: ${theme.colors.primary}; }`;
const FilterCount = styled.span`background: rgba(255,255,255,0.25); border-radius: ${theme.radii.full}; padding: 0 5px; font-size: 10px;`;

const TaskList = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[3]};`;
const TaskCard = styled(Card)`padding: 0; overflow: hidden; border-left: 3px solid ${p => ({
  pending: theme.colors.gray300, in_progress: theme.colors.info,
  waiting_response: theme.colors.warning, completed: theme.colors.success,
  failed: theme.colors.danger, escalated: theme.colors.danger, cancelled: theme.colors.gray300
})[p.$status] || theme.colors.gray300};`;
const TaskHeader = styled.div`display: flex; align-items: center; justify-content: space-between; padding: ${theme.spacing[4]} ${theme.spacing[5]}; cursor: pointer; gap: ${theme.spacing[3]}; &:hover { background: ${theme.colors.gray50}; }`;
const StatusDot = styled.div`width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; background: ${p => ({
  pending: theme.colors.gray400, in_progress: theme.colors.info,
  waiting_response: theme.colors.warning, completed: theme.colors.success,
  failed: theme.colors.danger, escalated: theme.colors.danger, cancelled: theme.colors.gray300
})[p.$status] || theme.colors.gray400};`;
const TaskInfo = styled.div`flex: 1; min-width: 0;`;
const TaskTitle = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin-bottom: ${theme.spacing[1]};`;
const TaskMeta = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; flex-wrap: wrap;`;
const MetaText = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const TaskDetail = styled.div`padding: ${theme.spacing[4]} ${theme.spacing[5]}; border-top: 1px solid ${theme.colors.border}; background: ${theme.colors.gray50};`;
const DetailGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: ${theme.spacing[3]};`;
const DetailItem = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]};`;
const DetailLabel = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.05em;`;
const DetailValue = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary};`;
const ResultBox = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.textSecondary}; background: ${theme.colors.white}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; padding: ${theme.spacing[3]}; line-height: 1.5;`;

const Textarea = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical; box-sizing: border-box; outline: none; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }`;
const ContextHint = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; margin-top: ${theme.spacing[1]};`;

const ScheduleToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  font-family: ${theme.typography.fontFamily};
  &:hover { border-color: ${theme.colors.borderHover}; background: ${theme.colors.white}; }
`;
const ScheduleToggleLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;
const ScheduleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
`;
const CronPreviewBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
`;
const CronPreviewLabel = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  font-weight: ${theme.typography.medium};
`;
const CronPreviewRaw = styled.code`
  font-size: 12px;
  font-family: ${theme.typography.fontMono};
  color: ${theme.colors.primary};
  background: ${theme.colors.gray50};
  padding: 2px 6px;
  border-radius: ${theme.radii.sm};
  display: inline-block;
`;
const CronPreviewDesc = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
  font-weight: ${theme.typography.medium};
`;
const CronErrorText = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.danger};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.dangerBorder};
  border-radius: ${theme.radii.md};
`;

const CronSection = styled.div`
  margin-bottom: ${theme.spacing[5]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.lg};
  background: ${theme.colors.white};
  overflow: hidden;
`;
const CronSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  cursor: pointer;
  &:hover { background: ${theme.colors.gray50}; }
`;
const CronSectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const CronList = styled.div`
  border-top: 1px solid ${theme.colors.border};
`;
const CronItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  &:last-child { border-bottom: none; }
  &:hover { background: ${theme.colors.gray50}; }
`;
const CronInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
  flex: 1;
  min-width: 0;
`;
const CronName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.medium};
  color: ${theme.colors.textPrimary};
`;
const CronMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;
const CronObjective = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  margin-top: ${theme.spacing[1]};
`;


